/**
 * Web Presence v2 — Craft.js layered builder.
 *
 * Isolated builder at /design/v2 (read-only render at /design/v2/view). Does NOT
 * touch the live Studio. Foundation for the layered/animated document model.
 *
 * Phase 1: Craft document model + canvas + toolbox + layers + per-node settings,
 *          hosting primitives AND the pixel-locked TrivenyHero as a node.
 * Phase 2 (this): tenant-scoped save/load persistence (GET/PUT /web-presence/craft)
 *          + a read-only renderer (CraftView). An ErrorBoundary keeps a runtime
 *          Craft error from blanking the page.
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { DeviceCtx, FRAMES, DEVICE_PRESETS, DEVICE_BUCKETS, type Device, type FrameKind } from './device';
import { BrandCtx, type Brand, type BrandToken } from './brand';
import { Editor, Frame, Element, useEditor, useNode } from '@craftjs/core';
import { Layers } from '@craftjs/layers';
import { Container, TextNode, ImageNode, TrivenyHeroNode, BlockNode, FreeContainer, FreeLayer, ShapeNode, VideoBgNode, SvgNode, PathNode } from './nodes';
import { Toolbox, SettingsPanel, AIGeneratePanel, BrandPanel, PageMetaPanel } from './panels';
import { webPresenceApi } from '../../shared/services/platformApi';
import { normalizeAIBlocks } from '../web-presence/utils/normalizeAIBlocks';
import './builder.css';

// Selection chrome for every node: hover/selected outline (CSS classes toggled
// on the node DOM) + a floating label with delete, so it's obvious which block
// is in edit mode. Wired via <Editor onRender>.
function RenderNode({ render }: { render: React.ReactElement }) {
  const { id } = useNode();
  const { actions, query, isActive } = useEditor((_s, q) => ({ isActive: q.getEvent('selected').contains(id) }));
  const { isHover, dom, name, deletable } = useNode((node) => ({
    isHover: node.events.hovered,
    dom: node.dom as HTMLElement | null,
    name: (node.data.custom && (node.data.custom as any).displayName) || node.data.displayName,
    deletable: query.node(node.id).isDeletable(),
  }));
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!dom) return;
    dom.classList.toggle('tv-node--selected', !!isActive);
    dom.classList.toggle('tv-node--hover', !!isHover && !isActive);
  }, [dom, isActive, isHover]);

  useEffect(() => {
    if (!dom || (!isActive && !isHover)) { setPos(null); return; }
    const update = () => { const r = dom.getBoundingClientRect(); setPos({ top: Math.max(r.top, 6), left: r.left }); };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => { window.removeEventListener('scroll', update, true); window.removeEventListener('resize', update); };
  }, [dom, isActive, isHover]);

  return (
    <>
      {pos && (isActive || isHover) && createPortal(
        <div style={{ position: 'fixed', top: pos.top, left: pos.left, transform: 'translateY(-100%)', zIndex: 2147483000, background: isActive ? '#7468ae' : '#a99fd6', color: '#fff', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, display: 'flex', gap: 8, alignItems: 'center', whiteSpace: 'nowrap' }}>
          <span>{name}</span>
          {isActive && deletable && (
            <button onClick={() => actions.delete(id)} title="Delete" style={{ background: 'rgba(255,255,255,.25)', border: 'none', color: '#fff', borderRadius: 4, cursor: 'pointer', fontSize: 11, lineHeight: 1, padding: '1px 5px' }}>✕</button>
          )}
        </div>,
        document.body,
      )}
      {render}
    </>
  );
}

// All node types MUST be in the resolver — the public CraftDocRenderer uses
// RESOLVER too, so deployed pages render correctly.
const RESOLVER = { Container, TextNode, ImageNode, TrivenyHeroNode, BlockNode, FreeContainer, FreeLayer, ShapeNode, VideoBgNode, SvgNode, PathNode };
// doc_key resolution: the site HOME deploys under 'home' (the public apex reads
// that key), so editing the home page uses 'home'. Any other page uses a
// per-page key so pages never collide on a single document.
//   /design/v2?page=<id>&home=1  → 'home'
//   /design/v2?page=<id>         → 'page:<id>'
//   /design/v2                   → 'home'
function resolveDocKey(): string {
  if (typeof window === 'undefined') return 'home';
  const p = new URLSearchParams(window.location.search);
  if (p.get('home') === '1') return 'home';
  const page = p.get('page');
  return page ? `page:${page}` : 'home';
}

const panelTitle: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
  textTransform: 'uppercase', color: '#9aa3af', marginBottom: 8,
};

// Small error boundary so a Craft.js runtime error shows a message, not a blank
// screen (the editor is third-party + this surface is new).
class Boundary extends React.Component<{ children: React.ReactNode }, { err?: Error }> {
  state: { err?: Error } = {};
  static getDerivedStateFromError(err: Error) { return { err }; }
  componentDidCatch(err: Error) { console.error('[CraftBuilder]', err); }
  render() {
    if (this.state.err) {
      return (
        <div style={{ padding: 40, fontFamily: 'system-ui', color: '#14171c' }}>
          <h2 style={{ margin: 0 }}>Builder failed to load</h2>
          <p style={{ color: '#6b7280' }}>{String(this.state.err?.message || this.state.err)}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

// Lives inside <Editor>; save / deploy / take-down. Initial load is handled by
// the parent (Frame data / page import) so we never double-load.
function PersistenceBar() {
  const { query } = useEditor();
  const [status, setStatus] = useState<string>('');
  const [moreOpen, setMoreOpen] = useState(false);
  const docKey = resolveDocKey();
  const previewHref = `/design/v2/view${typeof window !== 'undefined' ? window.location.search : ''}`;

  const save = async () => {
    setStatus('Saving…');
    try {
      const json = query.serialize();
      const res: any = await webPresenceApi.craftSave(JSON.parse(json), docKey);
      setStatus(res?.success === false ? 'Save failed (sign in?)' : 'Saved ✓');
    } catch {
      setStatus('Save failed');
    }
    window.setTimeout(() => setStatus(''), 2500);
  };

  // Explicit go-live: save the current draft, then publish it to the public site.
  const deploy = async () => {
    if (!window.confirm('Deploy this page to your public site? It goes live immediately at your public domain.')) return;
    setStatus('Deploying…');
    try {
      await webPresenceApi.craftSave(JSON.parse(query.serialize()), docKey);
      const res: any = await webPresenceApi.craftPublish(docKey);
      setStatus(res?.success === false ? 'Deploy failed (sign in?)' : 'Deployed ✓');
    } catch {
      setStatus('Deploy failed');
    }
    window.setTimeout(() => setStatus(''), 3000);
  };

  const takeDown = async () => {
    if (!window.confirm('Take this page off your public site? Visitors fall back to the built-in hero.')) return;
    setStatus('Taking down…');
    try {
      await webPresenceApi.craftUnpublish(docKey);
      setStatus('Taken down ✓');
    } catch {
      setStatus('Failed');
    }
    window.setTimeout(() => setStatus(''), 3000);
  };

  const ghost: React.CSSProperties = { padding: '7px 11px', borderRadius: 8, border: '1px solid #e6e8ec', background: '#fff', color: '#2a2f3a', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', textDecoration: 'none', whiteSpace: 'nowrap' };
  const item: React.CSSProperties = { display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', borderRadius: 7, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13 };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}>
      {status && <span style={{ fontSize: 11.5, color: status.includes('fail') || status.includes('Failed') ? '#dc2626' : '#16a34a', whiteSpace: 'nowrap', marginRight: 2 }}>{status}</span>}
      <button onClick={save} title="Save draft" style={ghost}>Save</button>
      <a href={previewHref} target="_blank" rel="noreferrer" title="Preview in a new tab" style={ghost}>Preview</a>
      <button onClick={deploy} title="Deploy to public site — goes live immediately" style={{ padding: '7px 13px', borderRadius: 8, border: 'none', background: '#7468ae', color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>Deploy</button>
      <button onClick={() => setMoreOpen((o) => !o)} title="More" style={{ ...ghost, padding: '7px 9px' }}>⋯</button>
      {moreOpen && (
        <div style={{ position: 'absolute', top: 42, right: 0, width: 210, background: '#fff', border: '1px solid #e6e8ec', borderRadius: 10, boxShadow: '0 12px 32px -12px rgba(20,23,28,0.25)', padding: 6, zIndex: 60 }}>
          <button onClick={() => { setMoreOpen(false); takeDown(); }} style={{ ...item, color: '#b4472b' }}>Take down from public site</button>
        </div>
      )}
    </div>
  );
}

// Public read-only renderer for a Craft document (used by the public site).
// Page meta: SEO ({title, description, ogImage}) + custom code snippets
// ({head, bodyEnd}). Shape matches the craft doc `meta`. Applies title + meta
// tags and injects the head/body code (recreating <script> nodes so they run),
// cleaning everything up on unmount. Editor preview passes no meta (skip).
type PageMeta = { seo?: { title?: string; description?: string; ogImage?: string }; scripts?: { head?: string; bodyEnd?: string } };

function injectCode(html: string, target: HTMLElement, marker: string): HTMLElement[] {
  if (!html || !html.trim()) return [];
  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  const inserted: HTMLElement[] = [];
  Array.from(tpl.content.childNodes).forEach((node) => {
    let el: HTMLElement;
    if (node.nodeName === 'SCRIPT') {
      // innerHTML-parsed scripts don't execute — recreate so they run.
      const s = document.createElement('script');
      const src = (node as HTMLScriptElement).getAttribute('src');
      if (src) s.src = src;
      Array.from((node as HTMLElement).attributes || []).forEach((a) => { if (a.name !== 'src') s.setAttribute(a.name, a.value); });
      s.text = (node as HTMLElement).textContent || '';
      el = s;
    } else if (node.nodeType === 1) {
      el = node as HTMLElement;
    } else {
      return;
    }
    el.setAttribute('data-wp-injected', marker);
    target.appendChild(el);
    inserted.push(el);
  });
  return inserted;
}

function usePageMeta(meta?: PageMeta) {
  useEffect(() => {
    if (!meta) return;
    const seo = meta.seo || {};
    const scripts = meta.scripts || {};
    const prevTitle = document.title;
    const created: HTMLElement[] = [];
    const upsertMeta = (selector: string, attrs: Record<string, string>) => {
      let tag = document.head.querySelector(selector) as HTMLMetaElement | null;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('data-wp-injected', 'seo');
        document.head.appendChild(tag);
        created.push(tag);
      }
      Object.entries(attrs).forEach(([k, v]) => tag!.setAttribute(k, v));
    };
    if (seo.title) document.title = seo.title;
    if (seo.description) upsertMeta('meta[name="description"][data-wp-injected]', { name: 'description', content: seo.description });
    if (seo.title) upsertMeta('meta[property="og:title"][data-wp-injected]', { property: 'og:title', content: seo.title });
    if (seo.description) upsertMeta('meta[property="og:description"][data-wp-injected]', { property: 'og:description', content: seo.description });
    if (seo.ogImage) upsertMeta('meta[property="og:image"][data-wp-injected]', { property: 'og:image', content: seo.ogImage });
    created.push(...injectCode(scripts.head || '', document.head, 'head'));
    created.push(...injectCode(scripts.bodyEnd || '', document.body, 'body'));
    return () => {
      document.title = prevTitle;
      created.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
    };
  }, [meta]);
}

// `brand` cascades the tenant brand colors to every block via BrandCtx — same
// mechanism the editor uses, so published pages match the builder preview.
// `meta` applies the page's SEO + custom code on the public render.
export function CraftDocRenderer({ document: doc, brand = {}, meta }: { document: any; brand?: Brand; meta?: PageMeta }) {
  usePageMeta(meta);
  const data = typeof doc === 'string' ? doc : JSON.stringify(doc);
  return (
    <Boundary>
      <BrandCtx.Provider value={brand}>
        <Editor resolver={RESOLVER} enabled={false}>
          <Frame data={data} />
        </Editor>
      </BrandCtx.Provider>
    </Boundary>
  );
}

export default function CraftBuilder() {
  // Fetch-first: resolve the initial canvas BEFORE mounting the Editor so we can
  // (a) load a saved Craft doc, or (b) MIGRATE — seed from a classic page's flat
  // content_blocks (?page=<id>), or (c) start from the default hero.
  const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const importPageId = params.get('page') || '';
  const [init, setInit] = useState<{ savedJson?: string; flatBlocks?: any[] } | null>(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      let savedJson: string | undefined;
      let flatBlocks: any[] | undefined;
      try {
        const r: any = await webPresenceApi.craftGet(resolveDocKey());
        const d = r?.data?.document;
        if (d) savedJson = typeof d === 'string' ? d : JSON.stringify(d);
      } catch { /* not signed in / none */ }
      if (!savedJson && importPageId) {
        try {
          const r: any = await webPresenceApi.getPage(importPageId);
          const cb = r?.data?.content_blocks;
          // The classic content_blocks use the backend's field shape (content, cta_primary,
          // button_text, form_id). The Craft renderer (BlockNode -> PublicSiteBlock) expects
          // the ContentBlock contract (richText, ctaButtons[]/buttons[], formId). Run the same
          // translator every other import path uses (panels.tsx AI-generate, PageEditor, etc.)
          // so body + CTA aren't silently dropped here.
          flatBlocks = Array.isArray(cb) ? normalizeAIBlocks(cb, importPageId) : [];
        } catch { /* page not found */ }
      }
      if (!cancel) setInit({ savedJson, flatBlocks });
    })();
    return () => { cancel = true; };
  }, [importPageId]);

  if (!init) {
    return <div style={{ padding: 40, fontFamily: 'system-ui', color: '#6b7280' }}>Loading builder…</div>;
  }

  return <BuilderShell init={init} importPageId={importPageId} />;
}

// Split out so the device-preview state can wrap the Editor + drive the canvas
// width and the per-device hide preview (via DeviceCtx).
// Per-medium export: capture the canvas to a PNG. Renders the CURRENT doc through the
// read-only CraftDocRenderer off-screen (no editor handles / grid / selection chrome)
// at the frame's width, then rasterizes it with html2canvas. Covers every frame —
// web, social, slide, story, print.
// Email HTML needs everything inline (clients strip <style>/classes) and can't do
// flex/grid. Walk the rendered DOM and bake a curated set of computed styles onto each
// element's `style`, coercing flex/grid → block so content still stacks. Mutates in
// place (the off-screen render is thrown away right after).
const EMAIL_STYLE_PROPS = [
  'color', 'background', 'background-color', 'background-image', 'background-size', 'background-position', 'background-repeat',
  'font-family', 'font-size', 'font-weight', 'font-style', 'line-height', 'letter-spacing', 'text-align', 'text-decoration', 'text-transform',
  'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
  'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
  'border-top-width', 'border-top-style', 'border-top-color', 'border-right-width', 'border-right-style', 'border-right-color',
  'border-bottom-width', 'border-bottom-style', 'border-bottom-color', 'border-left-width', 'border-left-style', 'border-left-color',
  'border-radius', 'box-shadow', 'width', 'max-width', 'height', 'min-height', 'vertical-align', 'opacity', 'object-fit', 'white-space',
];
function inlineComputedStyles(root: HTMLElement) {
  const els = [root, ...Array.from(root.querySelectorAll<HTMLElement>('*'))];
  els.forEach((el) => {
    if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE') return;
    const cs = window.getComputedStyle(el);
    const parts: string[] = [];
    for (const p of EMAIL_STYLE_PROPS) {
      const v = cs.getPropertyValue(p);
      if (!v || v === 'none' || v === 'normal' || v === 'auto' || v === '0px' || v === 'rgba(0, 0, 0, 0)') continue;
      parts.push(`${p}: ${v}`);
    }
    const disp = cs.display;
    if (disp === 'flex' || disp === 'inline-flex' || disp === 'grid' || disp === 'inline-grid') parts.push('display: block');
    else if (disp === 'inline-block' || disp === 'table' || disp === 'table-cell') parts.push(`display: ${disp}`);
    el.setAttribute('style', parts.join('; '));
    if (el.tagName === 'IMG') { const wpx = parseInt(cs.width, 10); if (wpx) el.setAttribute('width', String(wpx)); }
  });
}

function ExportButton({ frame, brand, width, height }: { frame: FrameKind; brand: Brand; width: number | string; height?: number }) {
  const { query } = useEditor();
  const [doc, setDoc] = useState<any>(null);
  const [mode, setMode] = useState<'png' | 'html' | 'pdf'>('png');
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const hiddenRef = React.useRef<HTMLDivElement>(null);
  const w = typeof width === 'number' ? width : 1080;

  const start = (m: 'png' | 'html' | 'pdf') => { setMenuOpen(false); try { setMode(m); setDoc(JSON.parse(query.serialize())); setBusy(true); } catch { /* ignore */ } };
  const download = (href: string, name: string) => { const a = document.createElement('a'); a.href = href; a.download = name; a.click(); };

  useEffect(() => {
    if (!doc) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const node = hiddenRef.current;
        if (!node) return;
        if (mode === 'png') {
          const html2canvas = (await import('html2canvas')).default;
          const canvas = await html2canvas(node, { backgroundColor: '#ffffff', scale: 2, useCORS: true, logging: false });
          if (cancelled) return;
          download(canvas.toDataURL('image/png'), `triveny-${frame}-${Date.now()}.png`);
        } else if (mode === 'pdf') {
          const html2canvas = (await import('html2canvas')).default;
          const canvas = await html2canvas(node, { backgroundColor: '#ffffff', scale: 2, useCORS: true, logging: false });
          if (cancelled) return;
          const { jsPDF } = await import('jspdf');
          const iw = canvas.width, ih = canvas.height;
          const pdf = new jsPDF({ orientation: iw >= ih ? 'landscape' : 'portrait', unit: 'px', format: [iw, ih] });
          pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, iw, ih);
          pdf.save(`triveny-${frame}-${Date.now()}.pdf`);
        } else {
          // HTML/email — bake computed styles inline (so it survives email clients that
          // strip <style>/classes) and flatten flex/grid to block, then wrap in a centered
          // table shell. Self-contained and drops into most email tools.
          inlineComputedStyles(node);
          const inner = node.innerHTML;
          const maxW = typeof width === 'number' ? width : 600;
          const html = `<!doctype html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:#f4f4f5;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;background:#f4f4f5;">
<tr><td align="center" style="padding:24px 12px;">
<table role="presentation" width="${maxW}" cellpadding="0" cellspacing="0" style="border-collapse:collapse;max-width:100%;background:#ffffff;">
<tr><td>${inner}</td></tr>
</table>
</td></tr></table>
</body>
</html>`;
          const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
          download(url, `triveny-email-${Date.now()}.html`);
          setTimeout(() => URL.revokeObjectURL(url), 4000);
        }
      } catch (e) { console.error('export failed', e); } finally { if (!cancelled) { setDoc(null); setBusy(false); } }
    }, 400); // let images / fonts settle before rasterizing
    return () => { cancelled = true; clearTimeout(t); };
  }, [doc, frame, mode, width]);

  const menuItem: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '8px 10px', borderRadius: 7, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, color: '#2a2f3a', textAlign: 'left' };
  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setMenuOpen((o) => !o)} disabled={busy} title="Export" style={{ padding: '7px 11px', borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', border: 'none', background: 'transparent', color: '#4a5160' }}>
        {busy ? '⤓ …' : '⤓ Export ▾'}
      </button>
      {menuOpen && !busy && (
        <div style={{ position: 'absolute', top: 40, right: 0, width: 190, background: '#fff', border: '1px solid #e6e8ec', borderRadius: 10, boxShadow: '0 12px 32px -12px rgba(20,23,28,0.25)', padding: 6, zIndex: 60 }}>
          <button onClick={() => start('png')} style={menuItem}>PNG image <span style={{ fontSize: 10, color: '#9aa3af', textTransform: 'capitalize' }}>{frame}</span></button>
          <button onClick={() => start('pdf')} style={menuItem}>PDF <span style={{ fontSize: 10, color: '#9aa3af' }}>print</span></button>
          <button onClick={() => start('html')} style={menuItem}>HTML <span style={{ fontSize: 10, color: '#9aa3af' }}>email</span></button>
        </div>
      )}
      {doc && createPortal(
        <div style={{ position: 'fixed', left: -99999, top: 0, zIndex: -1, pointerEvents: 'none' }}>
          <div ref={hiddenRef} style={{ width: w, minHeight: height, background: '#fff' }}>
            <CraftDocRenderer document={doc} brand={brand} />
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

function BuilderShell({ init, importPageId }: { init: { savedJson?: string; flatBlocks?: any[] }; importPageId: string }) {
  const [presetLabel, setPresetLabel] = useState('Laptop');
  const preset = DEVICE_PRESETS.find((p) => p.label === presetLabel) ?? DEVICE_PRESETS.find((p) => p.device === 'desktop')!;
  const device: Device = preset.device;
  const [leftOpen, setLeftOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(true);
  const [rightFloat, setRightFloat] = useState(false);
  const [floatPos, setFloatPos] = useState({ x: typeof window !== 'undefined' ? window.innerWidth - 360 : 900, y: 72 });
  const [showGrid, setShowGrid] = useState(false); // 12-column layout grid overlay
  const [frame, setFrame] = useState<FrameKind>('web'); // artifact frame preset
  const [menu, setMenu] = useState<null | 'ai' | 'brand' | 'seo'>(null); // top-bar popover
  // Brand: owned here so it provides via BrandCtx (cascade) AND drives BrandPanel.
  const [brand, setBrandState] = useState<Brand>({});
  useEffect(() => {
    webPresenceApi.craftGet('brand').then((r: any) => { const d = r?.data?.document; if (d && typeof d === 'object') setBrandState(d); }).catch(() => {});
  }, []);
  const setBrand = (next: Brand) => { setBrandState(next); webPresenceApi.craftSave(next, 'brand').catch(() => {}); };
  // Design tokens (wp_design_tokens) — the classic/AI store. Loaded here and
  // provided via BrandCtx so block color pickers surface the org's real tokens,
  // and the Brand panel can import-from / sync-to them (one connected system).
  const [siteId, setSiteId] = useState<string>('');
  const [tokens, setTokens] = useState<BrandToken[]>([]);
  const loadTokens = React.useCallback((sid: string) => {
    if (!sid) return;
    webPresenceApi.getDesignTokens(sid).then((r: any) => {
      const rows = r?.data ?? r ?? [];
      setTokens((Array.isArray(rows) ? rows : []).map((t: any) => ({ id: t.id, name: t.token_name || t.name, value: t.token_value || t.value, group: t.token_group || t.group })));
    }).catch(() => {});
  }, []);
  useEffect(() => {
    webPresenceApi.getSites().then((r: any) => {
      const rows = r?.data ?? r ?? [];
      const sid = Array.isArray(rows) && rows[0] ? rows[0].id : '';
      if (sid) { setSiteId(sid); loadTokens(sid); }
    }).catch(() => {});
  }, [loadTokens]);
  const isWeb = frame === 'web';
  const canvasWidth = isWeb ? preset.width : FRAMES[frame].width;
  const canvasHeight = isWeb ? undefined : FRAMES[frame].height;
  const toggleBtn = (label: string, on: boolean, onClick: () => void): React.ReactNode => (
    <button onClick={onClick} style={{ padding: '6px 10px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: on ? '1px solid #7468ae' : '1px solid #e6e8ec', background: on ? '#f7f4ff' : '#fff', color: on ? '#5b4b9e' : '#6b7280' }}>{label}</button>
  );
  const topBtn = (label: string, on: boolean, onClick: () => void): React.ReactNode => (
    <button onClick={onClick} style={{ padding: '7px 11px', borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', border: 'none', background: on ? '#f0ecfb' : 'transparent', color: on ? '#5b4b9e' : '#4a5160' }}>{label}</button>
  );
  const segBtn = (on: boolean): React.CSSProperties => ({ padding: '5px 11px', borderRadius: 7, fontSize: 13, cursor: 'pointer', border: on ? '1px solid #7468ae' : '1px solid #e6e8ec', background: on ? '#f7f4ff' : '#fff', color: on ? '#5b4b9e' : '#6b7280' });
  const selStyle: React.CSSProperties = { padding: '6px 10px', borderRadius: 7, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', border: '1px solid #7468ae', background: '#f7f4ff', color: '#5b4b9e' };
  const miniBtn: React.CSSProperties = { border: '1px solid #e6e8ec', background: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: 12, padding: '2px 6px' };
  const DEVICE_ICON: Record<Device, string> = { desktop: '🖥', tablet: '▭', mobile: '▯' };
  const monoFont = "'JetBrains Mono', ui-monospace, Menlo, monospace";
  // Drag the floating panel by its header.
  const onFloatDragDown = (e: React.PointerEvent) => {
    e.preventDefault();
    const sx = e.clientX, sy = e.clientY, ox = floatPos.x, oy = floatPos.y;
    const move = (ev: PointerEvent) => setFloatPos({ x: Math.max(0, Math.min(window.innerWidth - 60, ox + ev.clientX - sx)), y: Math.max(0, Math.min(window.innerHeight - 40, oy + ev.clientY - sy)) });
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  // Header shared by the docked + floating panel — consistent with the left panel's
  // fixed "Insert" header; when floating it doubles as the drag handle.
  const settingsHeader = (floating: boolean) => (
    <div
      onPointerDown={floating ? onFloatDragDown : undefined}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid #eef0f3', flexShrink: 0, cursor: floating ? 'grab' : 'default', userSelect: 'none' }}
    >
      <span style={panelTitle}>{floating ? '⠿ Properties' : 'Properties'}</span>
      <span style={{ display: 'flex', gap: 4 }}>
        <button title={rightFloat ? 'Dock panel' : 'Float panel'} onPointerDown={(e) => e.stopPropagation()} onClick={() => setRightFloat((v) => !v)} style={miniBtn}>{rightFloat ? '⊟' : '⤢'}</button>
        <button title="Close panel" onPointerDown={(e) => e.stopPropagation()} onClick={() => setRightOpen(false)} style={miniBtn}>✕</button>
      </span>
    </div>
  );
  return (
    <Boundary>
      <DeviceCtx.Provider value={device}>
      <BrandCtx.Provider value={{ ...brand, tokens }}>
      <Editor resolver={RESOLVER} onRender={RenderNode}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', fontFamily: "'Inter', system-ui, sans-serif", background: '#f3f3ef' }}>
          {/* ───────────── Top bar: identity · frame/device · page chrome ───────────── */}
          <header style={{ display: 'flex', alignItems: 'center', gap: 12, height: 52, flexShrink: 0, padding: '0 14px', borderBottom: '1px solid #e6e8ec', background: '#fff', position: 'relative', zIndex: 50 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: 220 }}>
              {!leftOpen && <button title="Open panels" onClick={() => setLeftOpen(true)} style={miniBtn}>▶</button>}
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#009a83', boxShadow: '12px 0 0 #46b1e5, 24px 0 0 #7468ae', flex: 'none' }} />
              <span style={{ marginLeft: 22, fontFamily: monoFont, fontSize: 11.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#5b4b9e', fontWeight: 700 }}>Studio</span>
              <span style={{ fontSize: 12, color: '#9aa3af' }}>· {importPageId && !init.savedJson ? 'imported' : resolveDocKey() === 'home' ? 'Home' : 'Page'}</span>
            </div>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <select value={frame} onChange={(e) => setFrame(e.target.value as FrameKind)} title="Canvas frame" style={selStyle}>
                {(Object.keys(FRAMES) as FrameKind[]).map((k) => <option key={k} value={k}>{FRAMES[k].label}</option>)}
              </select>
              {isWeb && (
                <>
                  <div style={{ display: 'flex', gap: 2, marginRight: 2 }}>
                    {(['mobile', 'tablet', 'desktop'] as Device[]).map((d) => (
                      <button
                        key={d}
                        onClick={() => setPresetLabel((DEVICE_PRESETS.find((p) => p.device === d && (d !== 'desktop' || p.label === 'Laptop')) ?? DEVICE_PRESETS.find((p) => p.device === d)!).label)}
                        title={DEVICE_BUCKETS[d]}
                        style={segBtn(device === d)}
                      >{DEVICE_ICON[d]}</button>
                    ))}
                  </div>
                  <select value={presetLabel} onChange={(e) => setPresetLabel(e.target.value)} title="Device" style={selStyle}>
                    <optgroup label="Mobile">
                      {DEVICE_PRESETS.filter((p) => p.device === 'mobile').map((p) => <option key={p.label} value={p.label}>{p.label} · {p.width}</option>)}
                    </optgroup>
                    <optgroup label="Tablet">
                      {DEVICE_PRESETS.filter((p) => p.device === 'tablet').map((p) => <option key={p.label} value={p.label}>{p.label} · {p.width}</option>)}
                    </optgroup>
                    <optgroup label="Desktop">
                      {DEVICE_PRESETS.filter((p) => p.device === 'desktop').map((p) => <option key={p.label} value={p.label}>{p.label} · {p.width}</option>)}
                    </optgroup>
                  </select>
                </>
              )}
              {toggleBtn('⋮⋮ Grid', showGrid, () => setShowGrid((v) => !v))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, position: 'relative' }}>
              {topBtn('✦ Assistant', menu === 'ai', () => setMenu(menu === 'ai' ? null : 'ai'))}
              {topBtn('Brand', menu === 'brand', () => setMenu(menu === 'brand' ? null : 'brand'))}
              {topBtn('SEO', menu === 'seo', () => setMenu(menu === 'seo' ? null : 'seo'))}
              {!rightOpen && topBtn('⚙ Properties', false, () => setRightOpen(true))}
              <ExportButton frame={frame} brand={brand} width={canvasWidth} height={canvasHeight} />
              <div style={{ width: 1, height: 22, background: '#e6e8ec', margin: '0 6px' }} />
              <PersistenceBar />
              {menu && (
                <div style={{ position: 'absolute', top: 46, right: 0, width: 330, maxHeight: '76vh', overflow: 'auto', background: '#fff', border: '1px solid #e6e8ec', borderRadius: 12, boxShadow: '0 16px 48px -16px rgba(20,23,28,0.28)', padding: 16, zIndex: 60 }}>
                  {menu === 'ai' && <AIGeneratePanel />}
                  {menu === 'brand' && <BrandPanel brand={brand} onChange={setBrand} tokens={tokens} siteId={siteId} onTokensSaved={() => loadTokens(siteId)} />}
                  {menu === 'seo' && <PageMetaPanel docKey={resolveDocKey()} />}
                </div>
              )}
            </div>
          </header>

          {/* ───────────── Body: left (layers/insert) · canvas · right (properties) ───────────── */}
          <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {/* LEFT — insert + layers ("left = layers/pages") */}
          {leftOpen && (
            <aside style={{ width: 248, flexShrink: 0, borderRight: '1px solid #e6e8ec', background: '#fff', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid #eef0f3', flexShrink: 0 }}>
                <span style={panelTitle}>Insert</span>
                <button title="Collapse" onClick={() => setLeftOpen(false)} style={miniBtn}>◀</button>
              </div>
              {/* single scroll container so the Layers tree at the bottom is always reachable */}
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 18 }}>
                <Toolbox />
                <div>
                  <div style={panelTitle}>Layers</div>
                  <div style={{ border: '1px solid #eef0f3', borderRadius: 8 }}>
                    <Layers expandRootOnLoad />
                  </div>
                </div>
              </div>
            </aside>
          )}

          {/* CENTER — canvas */}
          <main style={{ flex: 1, overflow: 'auto', padding: 24 }}>
            <div style={{ position: 'relative', width: '100%', maxWidth: canvasWidth, margin: '0 auto', background: '#fff', minHeight: canvasHeight ?? '100%', height: canvasHeight, boxShadow: '0 8px 24px -10px rgba(20,23,28,0.12)', borderRadius: 10, overflow: 'hidden', transition: 'max-width .2s ease' }}>
              {/* 12-column layout grid overlay (Paulo's design baseline) — non-interactive. */}
              {showGrid && (
                <div style={{ position: 'absolute', inset: 0, padding: '0 48px', display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 16, pointerEvents: 'none', zIndex: 40 }}>
                  {Array.from({ length: 12 }).map((_, i) => (
                    <div key={i} style={{ background: 'rgba(116,104,174,0.07)', borderLeft: '1px solid rgba(116,104,174,0.18)', borderRight: '1px solid rgba(116,104,174,0.18)' }} />
                  ))}
                </div>
              )}
              {/* data set → load saved doc; else children (migrated blocks or default). */}
              <Frame data={init.savedJson}>
                <Element is={Container} canvas background="#fafaf7" padding={0} minHeight={500}>
                  {init.flatBlocks && init.flatBlocks.length ? (
                    init.flatBlocks
                      .filter((b: any) => b && b.isVisible !== false)
                      .sort((a: any, b: any) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                      .map((b: any, i: number) => {
                        const bt = b.blockType || b.type || 'text';
                        // The Triveny hero is its OWN node (BlockNode can't render it).
                        if (bt === 'triveny_hero') {
                          const s = (b.content && b.content.statements) || {};
                          return <Element key={i} is={TrivenyHeroNode} design={s.Design} market={s.Market} engage={s.Engage} />;
                        }
                        return <Element key={i} is={BlockNode} blockType={bt} content={b.content || {}} styles={b.styles || {}} />;
                      })
                  ) : (
                    <Element is={TrivenyHeroNode} />
                  )}
                </Element>
              </Frame>
            </div>
          </main>

          {/* Right: properties — docked (header + scroll, matching the left panel) */}
          {rightOpen && !rightFloat && (
            <aside style={{ width: 300, flexShrink: 0, borderLeft: '1px solid #e6e8ec', background: '#fff', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {settingsHeader(false)}
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 14 }}>
                <SettingsPanel />
              </div>
            </aside>
          )}
        </div>
        {/* Floating properties — draggable by its header, header fixed + body scrolls */}
        {rightOpen && rightFloat && (
          <div style={{ position: 'fixed', left: floatPos.x, top: floatPos.y, width: 320, maxHeight: 'calc(100vh - 92px)', display: 'flex', flexDirection: 'column', background: '#fff', border: '1px solid #e6e8ec', borderRadius: 12, boxShadow: '0 16px 48px -16px rgba(20,23,28,0.35)', overflow: 'hidden', zIndex: 60 }}>
            {settingsHeader(true)}
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 14 }}>
              <SettingsPanel />
            </div>
          </div>
        )}
        </div>
      </Editor>
      </BrandCtx.Provider>
      </DeviceCtx.Provider>
    </Boundary>
  );
}

// Read-only public renderer for a saved Craft document. Reached at /design/v2/view.
export function CraftView() {
  const [data, setData] = useState<string | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'empty' | 'error'>('loading');
  useEffect(() => {
    let cancel = false;
    webPresenceApi.craftGet(resolveDocKey())
      .then((res: any) => {
        if (cancel) return;
        const doc = res?.data?.document;
        if (!doc) { setState('empty'); return; }
        setData(typeof doc === 'string' ? doc : JSON.stringify(doc));
        setState('ready');
      })
      .catch(() => !cancel && setState('error'));
    return () => { cancel = true; };
  }, []);

  if (state === 'loading') return <div style={{ padding: 40, fontFamily: 'system-ui', color: '#6b7280' }}>Loading…</div>;
  if (state === 'empty') return <div style={{ padding: 40, fontFamily: 'system-ui', color: '#6b7280' }}>Nothing published yet — save a page in the builder first.</div>;
  if (state === 'error' || !data) return <div style={{ padding: 40, fontFamily: 'system-ui', color: '#dc2626' }}>Couldn’t load the page.</div>;

  return (
    <Boundary>
      <Editor resolver={RESOLVER} enabled={false}>
        <Frame data={data} />
      </Editor>
    </Boundary>
  );
}
